import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import { SERVICE_SELECTION_OPTIONS } from '@rover/constants/src/translations/serviceSelector.constants';
import { Box, Button, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { ServiceSelectorV2 } from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/components/ServiceSelector';
import { GroomingHomepageSearchClick } from '@rover/react-lib/src/pages/grooming/analyticsEvents';
import GroomingNewBadge from '@rover/react-lib/src/pages/grooming/components/GroomingNewBadge';
import { GROOMING_SEARCH_URL } from '@rover/react-lib/src/pages/grooming/constants';
import useInGroomingPilot from '@rover/react-lib/src/pages/grooming/hooks/useInGroomingPilot';
import emitMetricOnCtaClick from '@rover/react-lib/src/pages/home/emitMetricOnCtaClick';
import HomePageSearchBoxLocationInput from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/connectors/LocationInputConnector';
import type { UpdateFiltersFunc } from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/UpdateFiltersFunc';
import { SpacesRequired } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useFlag from '@rover/shared/js/hooks/useFlag';
import type { ServiceLinks } from '@rover/shared/js/hooks/useServiceLinksShim';
import {
  MobileWebHomePageServiceTypeChange,
  MobileWebHomePageServiceTypeDropdownClick,
} from '@rover/shared/js/search/utilities/analyticsEvents';
import { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import type { Pet } from '@rover/types/src/Pet';
import type { ServiceType, ServiceTypeOption } from '@rover/types/src/ServiceType';

import { TrainingNeedsPills } from '../../../../training/components/TrainingNeedsPills/TrainingNeedsPills';

export interface NarrowDeviceSearchBoxProps {
  isAuthenticated: boolean;
  pets: Pet[];
  filters: HomepageSearchFilters;
  serviceType: ServiceType | undefined;
  serviceTypeOptions: ServiceTypeOption[];
  serviceLinksForCurrentRoute: ServiceLinks | null;
  updateFilters: UpdateFiltersFunc;
  submit: () => void;
  submitTraining: () => void;
  currentUserPostalCode?: string;
}

const NarrowDeviceSearchBox: FC<NarrowDeviceSearchBoxProps> = ({
  serviceType,
  serviceTypeOptions,
  filters,
  updateFilters,
  submit,
  submitTraining,
}) => {
  const { radii, text, space, textColor } = useTheme();
  const [isDropInOrWalking, setIsDropInOrWalking] = useState(false);
  const [isDogTraining, setIsDogTraining] = useState(false);
  // With the simplified search box, pet selector is removed and pet count is always 1.
  // Date scheduler is also removed from this component.
  const [serviceRecurrenceCount, setServiceRecurrenceCount] = useState(1);

  const { shouldShowTrainingEntrypoints } = useInTrainingPilot();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();
  const isTrainingSearchNeedsFiltersActive = useFlag(FLAGS.ROLLOUT_TRAINING_SEARCH_NEEDS_FILTERS);

  /** Note: This now only controls whether or not the training needs pills are displayed. */
  const isTrainingHomePageSearchInputKillerActive = useFlag(
    FLAGS.KILLSWITCH_TRAINING_MOBILE_HOMEPAGE_SEARCH_INPUT
  );

  const { setScreenReaderAnnouncement } = useA11yContext();

  const isGrooming =
    filters.serviceTypeSlug === SERVICE_TYPE_CHOICES.grooming && shouldShowGroomingEntrypoints;

  useEffect(() => {
    setIsDropInOrWalking(
      serviceType?.slug === SERVICE_TYPE_CHOICES.dropIn ||
        serviceType?.slug === SERVICE_TYPE_CHOICES.walking
    );
    setIsDogTraining(
      serviceType?.slug === SERVICE_TYPE_CHOICES.training && shouldShowTrainingEntrypoints
    );
  }, [serviceType?.slug, shouldShowTrainingEntrypoints]);

  // Keep spacesRequired in sync:
  // - Drop-in / Walking: driven by recurrence count (SpacesRequired control)
  // - All other services: always 1 (since pet selector is removed)
  useEffect(() => {
    const desiredSpacesRequired = isDropInOrWalking ? serviceRecurrenceCount : 1;

    if (desiredSpacesRequired !== filters?.spacesRequired) {
      updateFilters({ spacesRequired: desiredSpacesRequired });
    }
  }, [filters?.spacesRequired, isDropInOrWalking, serviceRecurrenceCount, updateFilters]);

  const groomingSearchUrl = useMemo((): string => {
    if (!isGrooming) return '';
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.centerlat) params.set('centerlat', filters.centerlat);
    if (filters.centerlng) params.set('centerlng', filters.centerlng);
    if (filters.locationType) params.set('locationType', filters.locationType);
    const paramString = params.toString();
    return paramString ? `${GROOMING_SEARCH_URL}?${paramString}` : GROOMING_SEARCH_URL;
  }, [isGrooming, filters.location, filters.centerlat, filters.centerlng, filters.locationType]);

  const handleChange = useCallback(
    (newFilters: Partial<HomepageSearchFilters>): void => {
      const updates: Partial<HomepageSearchFilters> = { ...newFilters };

      // Only allow user-driven spacesRequired changes for drop-in/walking.
      // Otherwise we keep it at 1 via the effect above.
      if (!isDropInOrWalking && 'spacesRequired' in updates) {
        delete updates.spacesRequired;
      }

      updateFilters(updates);

      if (isDropInOrWalking && newFilters.spacesRequired) {
        setServiceRecurrenceCount(newFilters.spacesRequired);
      }
    },
    [isDropInOrWalking, updateFilters]
  );

  const serviceTypes = useMemo((): ServiceTypeOption[] => {
    const hasMatchingServiceType = (option): boolean =>
      serviceTypeOptions.some((service) => service.value === option.value);

    return SERVICE_SELECTION_OPTIONS()
      .filter((option) => {
        if (option.value === SERVICE_TYPE_CHOICES.grooming) {
          return shouldShowGroomingEntrypoints;
        }
        if (option.value === SERVICE_TYPE_CHOICES.training) {
          return shouldShowTrainingEntrypoints && hasMatchingServiceType(option);
        }
        return hasMatchingServiceType(option);
      })
      .map((option) =>
        option.value === SERVICE_TYPE_CHOICES.grooming
          ? { ...option, labelBadge: GroomingNewBadge }
          : option
      );
  }, [serviceTypeOptions, shouldShowTrainingEntrypoints, shouldShowGroomingEntrypoints]);

  const handleSubmit = useCallback(() => {
    setScreenReaderAnnouncement(t`Form submitted. Navigating to Search page.`);

    if (isDogTraining) {
      emitMetricOnCtaClick('training-homepage-cta-click');
      submitTraining();
    } else {
      // Ensure the simplified defaults are present at submit time.
      // (spacesRequired is also kept in sync by the effect above.)
      handleChange({
        bypassInterstitial: true,
        spacesRequired: isDropInOrWalking ? serviceRecurrenceCount : 1,
      });

      submit();
    }
  }, [
    handleChange,
    isDogTraining,
    isDropInOrWalking,
    serviceRecurrenceCount,
    setScreenReaderAnnouncement,
    submit,
    submitTraining,
  ]);

  const shouldShowTrainingNeedsPills =
    isDogTraining &&
    isTrainingSearchNeedsFiltersActive &&
    !isTrainingHomePageSearchInputKillerActive;

  return (
    <Flex
      data-testid="narrow-device-search-box"
      flexDirection="column"
      background="primary"
      paddingY="6x"
      paddingX="4x"
      marginX="4x"
      variant="elevation_low"
      borderRadius={radii.primary}
    >
      {serviceTypeOptions.length > 0 && (
        <Box mb="6x">
          <ServiceSelectorV2
            label={t`Which service do you need?`}
            inputFontSize={text['200'].fontSize}
            serviceTypeOptions={serviceTypes}
            serviceType={filters.serviceTypeSlug}
            onOpen={() =>
              emitAnalyticsEvent(
                new MobileWebHomePageServiceTypeDropdownClick({
                  serviceType: filters.serviceTypeSlug,
                })
              )
            }
            onChange={(serviceTypeSlug) => {
              if (filters.serviceTypeSlug !== serviceTypeSlug) {
                emitAnalyticsEvent(
                  new MobileWebHomePageServiceTypeChange({
                    serviceType: serviceTypeSlug,
                  })
                );
              }
              handleChange({ serviceTypeSlug });
            }}
          />
        </Box>
      )}
      <Box mb="6x">
        <HomePageSearchBoxLocationInput
          updateFilters={handleChange}
          serviceType={serviceType}
          inputHeight={space['12x']}
          inputFontSize={text['200'].fontSize}
          placeholder={t`Add your address`}
          label={t`What's your address?`}
          labelMb={space['1x']}
          labelColor={textColor.primary}
          {...(filters.location ? { inputValue: filters.location } : {})}
        />
      </Box>
      {shouldShowTrainingNeedsPills && (
        <Box mb="6x">
          <TrainingNeedsPills
            title="What training needs do you have? (Optional)"
            subtitle="We'll show trainers with the right experience."
            titleSize="100"
            startCollapsed
            selectedBehaviors={filters.behaviors || []}
            selectedSkills={filters.skills || []}
            behaviorsOnChange={(behaviors) => {
              handleChange({ behaviors });
            }}
            skillsOnChange={(skills) => {
              handleChange({ skills });
            }}
          />
        </Box>
      )}
      {isDropInOrWalking && (
        <Box mb="6x">
          <SpacesRequired
            searchFilters={{
              serviceType: filters.serviceTypeSlug,
              spacesRequired: serviceRecurrenceCount,
            }}
            onChange={(newSpacesRequired) => handleChange({ spacesRequired: newSpacesRequired })}
            mb="4px"
          />
        </Box>
      )}
      {isGrooming ? (
        <Button
          data-testid="search-box-grooming-redirect"
          size="small"
          fullWidth
          variant="primary"
          href={groomingSearchUrl}
          onPress={() => {
            emitAnalyticsEvent(new GroomingHomepageSearchClick());
            emitMetricOnCtaClick('grooming-homepage-cta-click');
          }}
        >
          Search
        </Button>
      ) : (
        <Button
          data-testid="search-box-submit"
          size="small"
          fullWidth
          variant="primary"
          onPress={handleSubmit}
        >
          <Trans>Search</Trans>
        </Button>
      )}
    </Flex>
  );
};

export default NarrowDeviceSearchBox;
